import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";
import EmailVerificationNotice from "@/components/EmailVerificationNotice";
import { startGoogleSignIn, takeIntent } from "@/lib/authRedirect";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState(location.state?.authError || "");
  const [unverified, setUnverified] = useState(null);

  useEffect(() => {
    if (location.state?.authError) toast.error(location.state.authError);
  }, [location.state]);

  const dest = (role) => role === "cleaner" ? "/cleaner/dashboard" : ["admin", "super_admin"].includes(role) ? "/admin" : "/customer";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setAuthError("");
    setUnverified(null);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      const intended = takeIntent().dest;
      navigate(intended || dest(user.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "email_unverified") {
        setUnverified(detail);
        return;
      }
      const msg = formatApiError(detail);
      setAuthError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const google = () => startGoogleSignIn({ dest: localStorage.getItem("cc_post_login_dest") || undefined }); // eslint-disable-line no-unused-vars -- Google sign-in temporarily hidden; kept for easy restore

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/3965509/pexels-photo-3965509.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative p-14 h-full flex flex-col justify-between text-background">
          <Link to="/" className="flex items-center gap-2"><Wordmark light iconClass="h-8 w-auto" textClass="text-lg" /></Link>
          <div><h2 className="font-display text-4xl font-medium leading-tight">Your spotless space is one click away.</h2></div>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><Wordmark iconClass="h-8 w-auto" textClass="text-lg" /></Link>
          <h1 className="font-display text-3xl font-semibold">Welcome back</h1>
          <p className="text-muted-foreground mt-1">Log in to manage your bookings.</p>

          {unverified && (
            <div className="mt-5">
              <EmailVerificationNotice email={unverified.email} message={unverified.message}
                initialCooldown={unverified.cooldown_remaining ?? 60} />
            </div>
          )}

          {authError && (
            <div className="mt-5 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" role="alert" data-testid="auth-error">
              {authError}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4 mt-8">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="login-email" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="login-password" />
            <button type="submit" disabled={busy} className="w-full py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Log in
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            New here? <Link to="/register" className="text-accent font-medium">Create an account</Link>
          </p>
          <p className="text-center text-sm mt-2">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-accent" data-testid="forgot-password-link">Forgot your password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
